import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { fetchJSON, putJSON } from 'api/fetch';
import { RootState } from 'initializers/types';

import { getSliceState } from '../helpers';

import { SLICE_NAME } from './constants';
import { Properties, PropertiesParams, PropertiesState } from './types';

export const fetchScheduleProperties = createAsyncThunk<Properties>(
  `${SLICE_NAME}/fetchProperties`,
  async (_, { rejectWithValue }) => {
    try {
      const resp = await fetchJSON(`/schedule_builder/schedule_properties`);
      return resp;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
export const updateScheduleProperties = createAsyncThunk<
  Properties,
  PropertiesParams
>(
  `${SLICE_NAME}/updateProperties`,
  async (properties: PropertiesParams, { rejectWithValue }) => {
    try {
      const resp = await putJSON(`/schedule_builder/schedule_properties/1`, {
        data: { attributes: properties },
      });
      return resp.data.attributes;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const initialState: PropertiesState = {
  isWageForecastVisible: false,
  isEventsVisible: false,
  isHoursForecastVisible: false,
  isPeopleForecastVisible: false,
  isScheduleUsersOnly: false,
  isSalesForecastEnabled: false,
  isWeatherForecastEnabled: false,
  isDepartmentsForecastEnabled: false,
  isWagePercentageForecastEnabled: false,
  isBudgetForecastEnabled: false,
  status: 'idle',
  error: undefined,
};

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchScheduleProperties.pending, state => {
      state.status = 'pending';
      state.error = undefined;
    });
    builder.addCase(fetchScheduleProperties.fulfilled, (state, action) => {
      Object.assign(state, action.payload);
      state.status = 'succeeded';
      state.error = undefined;
    });
    builder.addCase(fetchScheduleProperties.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || 'Failed to get schedule properties';
    });
    builder.addCase(updateScheduleProperties.pending, state => {
      state.status = 'pending';
      state.error = undefined;
    });
    builder.addCase(updateScheduleProperties.fulfilled, (state, action) => {
      Object.assign(state, action.payload);
      state.status = 'succeeded';
      state.error = undefined;
    });
    builder.addCase(updateScheduleProperties.rejected, (state, action) => {
      state.status = 'failed';
      state.error =
        action.error.message || 'Failed to update schedule properties';
    });
  },
});

export const { reducer } = slice;

export const selectPropertiesAttribute = <T extends keyof Properties>({
  state,
  attribute,
}: {
  state: RootState;
  attribute: T;
}): Properties[T] | undefined => {
  const propertiesState = getSliceState(state)?.[SLICE_NAME] || initialState;
  return propertiesState[attribute];
};

export const selectPropertiesIsLoaded = (state: RootState): boolean =>
  getSliceState(state)?.[SLICE_NAME]?.status === 'succeeded';
